// components/RoomsBooking.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Rating,
  TextField,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Close,
  Person,
  Wifi,
  AcUnit,
  LocalBar,
  Tv,
  RoomService,
  Pool,
  FitnessCenter,
  Restaurant,
  LocalParking,
  Spa,
  KingBed,
  Bathtub,
  SquareFoot,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Import components
import TopBar from '../website/TopBar';
import NavigationBar from '../website/NavigationBar';
import Footer from '../website/Footer';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const HOTEL_CODE = process.env.REACT_APP_HOTEL_CODE;
const serverRoot = process.env.REACT_APP_SERVER_ROOT;

// SafeImage Component for proper image handling (same as homepage)
const SafeImage = ({ src, alt, fallback, ...props }) => {
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) return imagePath;
    
    const serverRoot = process.env.REACT_APP_SERVER_ROOT;
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    
    return `${serverRoot}${cleanPath}`;
  };

  useEffect(() => {
    if (src) {
      const resolvedUrl = getImageUrl(src);
      setImageUrl(resolvedUrl);
      setImageError(false);
      
      // Preload image to check if it exists
      const img = new Image();
      img.onload = () => setImageError(false);
      img.onerror = () => setImageError(true);
      img.src = resolvedUrl;
    } else {
      setImageError(true);
    }
  }, [src]);

  if (imageError || !imageUrl) {
    return fallback;
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      onError={() => setImageError(true)}
      {...props}
    />
  );
};

// Room Features Icons Mapping
const roomFeatures = {
  wifi: { icon: <Wifi />, label: 'Free WiFi' },
  ac: { icon: <AcUnit />, label: 'Air Conditioning' },
  minibar: { icon: <LocalBar />, label: 'Minibar' },
  tv: { icon: <Tv />, label: 'Smart TV' },
  service: { icon: <RoomService />, label: '24/7 Room Service' },
  pool: { icon: <Pool />, label: 'Pool Access' },
  gym: { icon: <FitnessCenter />, label: 'Gym Access' },
  restaurant: { icon: <Restaurant />, label: 'Restaurant' },
  parking: { icon: <LocalParking />, label: 'Parking' },
  spa: { icon: <Spa />, label: 'Spa Access' },
};

// Room Card Component
const RoomCard = ({ room, onBookNow, onViewDetails }) => {
  const fallbackImage = 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';

  return (
    <Card sx={{ 
      transition: 'all 0.3s ease', 
      overflow: 'hidden',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid',
      borderColor: 'divider',
      '&:hover': { 
        transform: 'translateY(-8px)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        borderColor: 'primary.main'
      } 
    }}>
      {/* Image with Badge */}
      <Box sx={{ position: 'relative', height: 240, overflow: 'hidden' }}>
        <SafeImage
          src={room.images && room.images[0]}
          alt={room.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
          }}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          fallback={
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No Image Available
              </Typography>
            </Box>
          }
        />
        
        {/* Status Badge */}
        <Chip
          label={room.status || 'Available'}
          color={room.status === 'Available' ? 'success' : 'error'}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            fontWeight: 'bold'
          }}
        />
      </Box>
      
      <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header with Price */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
              {room.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {room.type}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="h6" color="primary" fontWeight="bold">
              ₦{room.price}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              /night
            </Typography>
          </Box>
        </Box>
        
        {/* Description */}
        <Typography variant="body2" color="text.secondary" paragraph sx={{ flexGrow: 1, mb: 2 }}>
          {room.description || 'Experience luxury and comfort in our beautifully appointed room with premium amenities.'}
        </Typography>

        {/* Room Features */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip 
            icon={<Person />} 
            label={`${room.capacity} Guests`} 
            size="small" 
            variant="outlined" 
          />
          {room.size && (
            <Chip 
              icon={<SquareFoot />} 
              label={`${room.size} m²`} 
              size="small" 
              variant="outlined" 
            />
          )}
          <Chip 
            icon={<KingBed />} 
            label={room.bedType || 'King Bed'} 
            size="small" 
            variant="outlined" 
          />
        </Box>

        {/* Rating and Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Rating value={parseFloat(room.rating) || 4.5} readOnly size="small" />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({room.reviews || '24'})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => onViewDetails(room)}
              sx={{ borderRadius: 2 }}
            >
              Details
            </Button>
            <Button 
              variant="contained" 
              size="small"
              onClick={() => onBookNow(room)}
              disabled={room.status !== 'Available'}
              sx={{ borderRadius: 2 }}
            >
              Book Now
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Main RoomsBooking Component
const RoomsBooking = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    minPrice: '',
    maxPrice: '',
    capacity: '',
    sortBy: 'name'
  });

  useEffect(() => {
    loadRooms();
  }, []);

  useEffect(() => {
    filterRooms();
  }, [rooms, filters]);

  const loadRooms = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_BASE_URL}/hotel/rooms/index.php`, {
        params: { 
          hotel_code: HOTEL_CODE,
          status: 'Available'
        }
      });
      
      if (response.data.success) {
        const roomsData = response.data.rooms || [];
        // Add some mock data for demonstration
        const enhancedRooms = roomsData.map(room => ({
          ...room,
          reviews: Math.floor(Math.random() * 100) + 10,
          bedType: ['King Bed', 'Queen Bed', 'Twin Beds'][Math.floor(Math.random() * 3)],
          features: ['wifi', 'ac', 'tv', 'minibar', 'service'].slice(0, Math.floor(Math.random() * 5) + 1)
        }));
        
        setRooms(enhancedRooms);
      } else {
        setError(response.data.error || 'Failed to load rooms');
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
      setError('Failed to load rooms. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filterRooms = () => {
    let filtered = [...rooms];

    // Apply filters
    if (filters.type) {
      filtered = filtered.filter(room => room.type === filters.type);
    }
    if (filters.minPrice) {
      filtered = filtered.filter(room => room.price >= parseInt(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(room => room.price <= parseInt(filters.maxPrice));
    }
    if (filters.capacity) {
      filtered = filtered.filter(room => room.capacity >= parseInt(filters.capacity));
    }

    // Apply sorting
    switch (filters.sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 4.5) - (a.rating || 4.5));
        break;
      default:
        filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    setFilteredRooms(filtered);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleBookNow = (room) => {
    // Navigate to booking page with room data, similar to HotelWebsite component
    navigate('/booking', { state: { room } });
  };

  const handleViewDetails = (room) => {
    setSelectedRoom(room);
    setDetailDialogOpen(true);
  };

  const RoomDetailDialog = ({ open, onClose, room }) => {
    if (!room) return null;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {room.name}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <SafeImage
                src={room.images && room.images[0]}
                alt={room.name}
                style={{
                  width: '100%',
                  height: 400,
                  objectFit: 'cover',
                  borderRadius: 12,
                }}
                fallback={
                  <Box
                    sx={{
                      width: '100%',
                      height: 400,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      borderRadius: 12
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No Image Available
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom color="text.primary">
                {room.type}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Rating value={parseFloat(room.rating) || 4.5} readOnly />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({room.reviews || '24'} reviews)
                </Typography>
              </Box>

              <Typography variant="body1" paragraph>
                {room.description || 'Experience the perfect blend of luxury and comfort in our beautifully appointed room. Featuring premium amenities and stunning views, this room is designed to provide an unforgettable stay.'}
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <Person color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Capacity" secondary={`${room.capacity} Guests`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SquareFoot color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Room Size" secondary={`${room.size || '45'} m²`} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <KingBed color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Bed Type" secondary={room.bedType || 'King Bed'} />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Amenities
              </Typography>
              <Grid container spacing={1}>
                {(room.features || []).map((feature, index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {roomFeatures[feature]?.icon}
                      <Typography variant="body2">
                        {roomFeatures[feature]?.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" color="primary" fontWeight="bold">
                ₦{room.price} <Typography component="span" variant="body1" color="text.secondary">/ night</Typography>
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => {
                onClose();
                handleBookNow(room);
              }}
              sx={{ borderRadius: 2, px: 4 }}
            >
              Book Now
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Top Bar */}
      <TopBar />

      {/* Navigation */}
      <NavigationBar />

      {/* Main Content */}
      <Box sx={{ py: 4 }}>
        <Container maxWidth="xl">
          {/* Header */}
          <Box textAlign="center" sx={{ mb: 6 }}>
            <Typography variant="h2" fontWeight="bold" color="primary" gutterBottom>
              Rooms & Suites
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Discover our luxurious accommodations designed for your comfort and enjoyment
            </Typography>
          </Box>

          {/* Filters */}
          <Paper sx={{ p: 3, mb: 6, borderRadius: 3, boxShadow: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  select
                  label="Room Type"
                  value={filters.type}
                  onChange={handleFilterChange('type')}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="Standard Room">Standard Room</MenuItem>
                  <MenuItem value="Deluxe Room">Deluxe Room</MenuItem>
                  <MenuItem value="Executive Suite">Executive Suite</MenuItem>
                  <MenuItem value="Presidential Suite">Presidential Suite</MenuItem>
                  <MenuItem value="Family Room">Family Room</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Min Price"
                  value={filters.minPrice}
                  onChange={handleFilterChange('minPrice')}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₦</Typography> }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Price"
                  value={filters.maxPrice}
                  onChange={handleFilterChange('maxPrice')}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₦</Typography> }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Guests"
                  value={filters.capacity}
                  onChange={handleFilterChange('capacity')}
                  inputProps={{ min: 1, max: 10 }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  select
                  label="Sort By"
                  value={filters.sortBy}
                  onChange={handleFilterChange('sortBy')}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="price-low">Price: Low to High</MenuItem>
                  <MenuItem value="price-high">Price: High to Low</MenuItem>
                  <MenuItem value="rating">Rating</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setFilters({
                    type: '',
                    minPrice: '',
                    maxPrice: '',
                    capacity: '',
                    sortBy: 'name'
                  })}
                  sx={{ height: '56px' }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Rooms Grid */}
          {loading ? (
            <Box textAlign="center" py={8}>
              <CircularProgress size={60} />
              <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                Loading luxurious rooms...
              </Typography>
            </Box>
          ) : filteredRooms.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Typography variant="h4" color="text.secondary" gutterBottom>
                No rooms found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Try adjusting your filters to see more options
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => setFilters({
                  type: '',
                  minPrice: '',
                  maxPrice: '',
                  capacity: '',
                  sortBy: 'name'
                })}
              >
                Clear All Filters
              </Button>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {filteredRooms.map((room) => (
                <Grid item xs={12} md={6} lg={4} key={room.id}>
                  <RoomCard 
                    room={room} 
                    onBookNow={handleBookNow}
                    onViewDetails={handleViewDetails}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Room Detail Dialog */}
          <RoomDetailDialog
            open={detailDialogOpen}
            onClose={() => setDetailDialogOpen(false)}
            room={selectedRoom}
          />
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default RoomsBooking;